import React from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { CollateralBasketStatus, CollateralStatus, Instrument, OrderDetail } from 'api'
import { Field } from 'view/components/inputs'
import { Combobox, ComboboxProps } from 'view/components/options'
import { InstrumentHelper, isCollateral } from 'model'
import {
  createInstrumentGetterSelector,
  createInstrumentListForStagingCollateralSelector,
  createInstrumentIdListSelector,
  createInstrumentListForInstrumentFiltersSelector
} from 'selectors/instrumentSelectors'
import { Locale, Translation } from 'locale'
import { AxeDetail } from 'selectors/axeSelectors'
import { createInstrumentFiltersForAxeCollateralSelector } from 'selectors/instrumentSelectors/instrumentFiltersForAxeCollateralSelector'
import { useParametricSelector } from 'utils'

const DEFAULT_PROPS = {
  getOptionText: InstrumentHelper.getName,
  getOptionDisabled: (instrument: Instrument) =>
    isCollateral(instrument) ? instrument.status !== CollateralStatus.ENABLED : instrument.status !== CollateralBasketStatus.ACTIVE,
  filterFields: ['isin', 'cusip', 'collateralBasketIsin', 'collateralBasketCusip'],
  noItemsText: Locale.translate(Translation.SC_N_COLLATERALS, { count: 0 })
}

interface InstrumentComboboxProps extends Omit<ComboboxProps<Instrument, string>, 'optionKeys' | 'getOption'> {
  fundId?: string | null | undefined
  instrumentIdFilter?: (id: string) => boolean
  isVirtualized?: boolean
}

const instrumentFundIdSelector = (_: {}, props: InstrumentComboboxProps) => props.fundId
const isVirtualizedSelector = (_: {}, props: InstrumentComboboxProps) => props.isVirtualized
const instrumentIdFilterSelector = (_: {}, props: InstrumentComboboxProps) => props.instrumentIdFilter

const instrumentOptionKeysSelector = createSelector(
  [createInstrumentIdListSelector(instrumentFundIdSelector), instrumentIdFilterSelector, isVirtualizedSelector],
  (instrumentIds, instrumentIdFilter) => {
    return instrumentIdFilter ? instrumentIds.filter(instrumentIdFilter) : instrumentIds
  }
)

export const InstrumentCombobox = React.forwardRef((props: InstrumentComboboxProps, ref: React.Ref<Combobox>) => {
  const getOption = useSelector(createInstrumentGetterSelector)
  const optionKeys = useParametricSelector(instrumentOptionKeysSelector, props)

  return (
    <Combobox
      ref={ref}
      {...props}
      getOption={getOption}
      optionKeys={optionKeys}
      getOptionText={props.getOptionText ?? DEFAULT_PROPS.getOptionText}
      filterFields={props.filterFields ?? DEFAULT_PROPS.filterFields}
      noItemsText={props.noItemsText ?? DEFAULT_PROPS.noItemsText}
    />
  )
})
export const InstrumentComboboxField = Field(InstrumentCombobox)

interface StagingCollateralComboboxProps extends Omit<ComboboxProps<Instrument, string>, 'optionKeys' | 'getOption'> {
  orderDetail: OrderDetail
}

const stagingOptionKeysSelector = createInstrumentListForStagingCollateralSelector((_, orderDetail: OrderDetail) => orderDetail)

export const StagingCollateralCombobox = React.forwardRef((props: StagingCollateralComboboxProps, ref: React.Ref<Combobox>) => {
  const getOption = useSelector(createInstrumentGetterSelector)
  const optionKeys = useParametricSelector(stagingOptionKeysSelector, props.orderDetail)

  return (
    <Combobox
      ref={ref}
      {...props}
      getOption={getOption}
      optionKeys={optionKeys}
      getOptionText={props.getOptionText ?? DEFAULT_PROPS.getOptionText}
      filterFields={props.filterFields ?? DEFAULT_PROPS.filterFields}
      noItemsText={props.noItemsText ?? DEFAULT_PROPS.noItemsText}
    />
  )
})

interface AxeCollateralComboboxProps extends Omit<ComboboxProps<Instrument, string>, 'optionKeys' | 'getOption'> {
  axeDetail: AxeDetail
}

const axeOptionKeysSelector = createInstrumentListForInstrumentFiltersSelector(
  createInstrumentFiltersForAxeCollateralSelector((_, axeDetail: AxeDetail) => axeDetail)
)

export const AxeCollateralCombobox = React.forwardRef((props: AxeCollateralComboboxProps, ref: React.Ref<Combobox>) => {
  const getOption = useSelector(createInstrumentGetterSelector)
  const optionKeys = useParametricSelector(axeOptionKeysSelector, props.axeDetail)

  return (
    <Combobox
      ref={ref}
      {...props}
      getOption={getOption}
      optionKeys={optionKeys}
      getOptionText={props.getOptionText ?? DEFAULT_PROPS.getOptionText}
      filterFields={props.filterFields ?? DEFAULT_PROPS.filterFields}
      noItemsText={props.noItemsText ?? DEFAULT_PROPS.noItemsText}
    />
  )
})
